import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { fieldLabelStyle } from './fieldStyles';
import { 
  Box, 
  Button, 
  Typography, 
  FormControl, 
  FormHelperText,
  IconButton,
  Paper,
  CircularProgress
} from '@mui/material';
import { 
  CloudUpload as UploadIcon, 
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { API_PATHS } from '../../utils/constants';
import { DOCUMENT_TYPE_MAPPING } from '../../Configurations/StatFieldData';

/**
 * FileUploadField component for handling file uploads in workflow forms
 */
const FileUploadField = ({
  id,
  name,
  label,
  required,
  disabled,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  accept = '*',
  maxSize = 5 * 1024 * 1024, // 5MB default
}) => {
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState('');
  
  // Generate a unique ID for the hidden file input
  const inputId = `file-input-${id || name}`;
  
  // Handle file selection
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    
    if (!file) return;
    
    // Validate file size
    if (file.size > maxSize) {
      setFileError(`File size exceeds the maximum allowed (${Math.round(maxSize / 1024 / 1024)}MB)`);
      return;
    }
    
    setFileError('');
    setUploading(true);
    
    try {
      // Upload file immediately like in the ticket system
      const formData = new FormData();
      formData.append("file", file);
      formData.append("fileType", "TICKET_DOC"); // Use same fileType as ticket system
      
      // Derive context
      let employeeUid;
      try {
        const ctx = (window.currentFormData || {});
        employeeUid = ctx.employeeUid ||
                      (ctx.selectedEmployee && ctx.selectedEmployee.employeeId) ||
                      (window.selectedEmployee && window.selectedEmployee.employeeId) ||
                      window.currentEmployeeId ||
                      window.currentEmployeeUid;
      } catch (_) {}
      
      // Decide fileType based on context: service docs for generic fields, user docs when tied to an employee
      const knownDocField = DOCUMENT_TYPE_MAPPING[name];
      const isKnownEmployeeDoc = knownDocField !== undefined;
      const fileTypeToUse = (employeeUid || isKnownEmployeeDoc) ? 'USER_DOCUMENT' : 'SERVICE_DOCUMENT';
      formData.append('fileType', fileTypeToUse);
      
      // Provide helpful context to backend
      try { formData.append('fileName', file.name); } catch (e) {}
      if (employeeUid) {
        formData.append('employeeUid', employeeUid);
      }
      // Map a recognizable document type to snake_case for backend when applicable
      const toSnakeFromCamel = (s) => s.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
      let documentTypeSnake;
      if (isKnownEmployeeDoc) {     
        documentTypeSnake =   toSnakeFromCamel(knownDocField)
        formData.append('documentType', documentTypeSnake);
      }
      

      // Use API_PATHS constant for environment-aware URL
      const response = await fetch(`${API_PATHS.V1}/files/upload`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwt_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`File upload failed with status: ${response.status}`);
      }
      
      const responseData = await response.json(); // Parse JSON response
      console.log(`File uploaded successfully, received response:`, responseData);
      
      // Extract file metadata from the API response
      let fileMetadata = null;

      if (responseData.success && responseData.data) {
        fileMetadata = {
          originalName: responseData.data.originalName || file.name,
          fileName: responseData.data.fileName || file.name,
          fileSize: responseData.data.fileSize || file.size,
          contentType: responseData.data.contentType || file.type,
          filePath: responseData.data.filePath || responseData.data.path || null,
          path: responseData.data.path || responseData.data.filePath || null,
          fileType: responseData.data.fileType || fileTypeToUse,
          documentType: responseData.data.documentType || (isKnownEmployeeDoc ? documentTypeSnake : undefined),
          id: responseData.data.id || responseData.data.documentId || null,
          uploadDate: new Date().toISOString(),
          // Common flags used by other document components
          hasFile: true,
          uploadStatus: 'UPLOADED',
          isReference: false,
          documentName: (responseData.data.originalName || responseData.data.fileName || file.name)
        };
        console.log(`Extracted file metadata:`, fileMetadata);
      } else {
        console.error('Invalid file upload response structure:', responseData);
        throw new Error('Invalid response from file upload API');
      }
      
      // Call the onChange handler with the parsed file metadata
      onChange({
        target: {
          name,
          value: fileMetadata
        }
      });
    } catch (error) {
      console.error("Error uploading file:", error);
      setFileError("Error uploading file: " + (error.message || "Unknown error"));
    } finally {
      setUploading(false);
    }
  };
  
  // Handle file removal
  const handleRemoveFile = () => {
    onChange({
      target: {
        name,
        value: null
      }
    });
  };
  
  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  };
  
  return (
    <FormControl 
      fullWidth 
      error={!!error || !!fileError} 
      disabled={disabled}
      sx={{ mb: 2 }}
    >
      <Typography 
        component="label" 
        htmlFor={inputId} 
        sx={{ 
          ...fieldLabelStyle,
          mb: 1, 
          display: 'block',
          color: (theme) => error || fileError ? theme.palette.error.main : 'inherit'
        }}
      >
        {label} {required && <Box component="span" sx={{ color: 'error.main' }}>*</Box>}
      </Typography>
      
      {/* Hidden file input */}
      <input
        id={inputId}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        onBlur={onBlur}
        style={{ display: 'none' }}
        disabled={disabled || uploading}
      />
      
      {/* File upload button or file display */}
      {(value && value?.target?.value?.fileName)? (
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            borderColor: (theme) => error || fileError ? theme.palette.error.main : 'inherit'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FileIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {(() => {
                  const path = value?.path || value?.filePath;              
                  const fromPath = path ? String(path).split(/[\\/]/).pop() : '';
                  return (
                    value?.originalName ||
                    value?.fileName ||
                    value?.name ||
                    value?.documentName ||
                    value?.target?.value?.fileName ||
                    value?.target?.name ||
                    fromPath ||
                    value?.documentType ||
                    'File'
                  );
                })()}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {typeof (value?.fileSize ?? value?.size) === 'number' ? formatFileSize(value.fileSize ?? value.size) : ''}
              </Typography>
            </Box>
          </Box>
          
          <IconButton 
            onClick={handleRemoveFile} 
            disabled={disabled}
            size="small"
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Paper>
      ) : (
        <Button
          component="label"
          htmlFor={inputId}
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon sx={{ fontSize: 24 }} />}
          disabled={disabled || uploading}
          fullWidth
          sx={{ 
            py: 1.5,
            borderStyle: 'dashed',
            borderColor: (theme) => error || fileError ? theme.palette.error.main : theme.palette.primary.main,
            '& .MuiButton-startIcon': {
              display: 'inline-flex',
              visibility: 'visible !important',
              marginRight: 1,
              color: 'primary.main'
            }
          }}
        >
          {uploading ? 'Uploading...' : 'Upload File'}
        </Button>
      )}
      
      {/* Error message */}
      {(error || fileError) && (
        <FormHelperText error>
          {fileError || helperText || 'Please select a valid file'}
        </FormHelperText>
      )}
      
      {/* Helper text (when no error) */}
      {!error && !fileError && helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

FileUploadField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  value: PropTypes.object, // File object
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  helperText: PropTypes.string,
  accept: PropTypes.string, // File types to accept
  maxSize: PropTypes.number, // Maximum file size in bytes
};

export default FileUploadField;
